import React, { useRef, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { gsap } from "gsap";

interface SearchBarProps {
  placeholder?: string;
  value?: string;
  onSearch?: (query: string) => void;
}

export function SearchBar({
  placeholder = "Search operational guides, bidding formulas, SOPs, and scripts...",
  value,
  onSearch,
}: SearchBarProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState(value || "");
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (value !== undefined) {
      setQuery(value);
    }
  }, [value]);

  useEffect(() => {
    // GSAP reveal animation on mount
    if (containerRef.current) {
      gsap.fromTo(
        containerRef.current,
        {
          opacity: 0,
          y: 20,
          scale: 0.95,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          ease: "power3.out",
          delay: 0.2,
        }
      );
    }
  }, []);

  const handleChange = (newQuery: string) => {
    setQuery(newQuery);
    if (onSearch) {
      onSearch(newQuery);
    }
  };

  const handleSearchTagClick = (tag: string) => {
    handleChange(tag);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(query.trim());
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div
        ref={containerRef}
        className={`relative mx-auto max-w-2xl transition-all duration-300 ${
          isFocused ? "scale-[1.02] transform" : ""
        }`}
      >
        <div
          className={`relative flex items-center rounded-2xl border-2 bg-white shadow-lg transition-all duration-300 ${
            isFocused
              ? "border-blue-500 shadow-blue-100"
              : "border-slate-200 hover:border-slate-300"
          }`}
        >
          <Search
            className={`absolute left-5 h-5 w-5 transition-colors duration-200 ${
              isFocused ? "text-blue-500" : "text-slate-400"
            }`}
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleChange(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={placeholder}
            className="w-full bg-transparent py-4 pl-14 pr-4 text-base text-slate-900 placeholder-slate-400 focus:outline-none md:text-lg"
          />
          {query && (
            <button
              type="submit"
              className="absolute right-3 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              Search
            </button>
          )}
        </div>

        {/* Popular searches hint */}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5 text-center text-sm text-slate-500">
          <span className="font-medium text-slate-400">Popular:</span>
          <button
            type="button"
            onClick={() => handleSearchTagClick("Bidding Calculator")}
            className="cursor-pointer font-medium text-blue-600 hover:underline"
          >
            Bidding Formula
          </button>
          <span>•</span>
          <button
            type="button"
            onClick={() => handleSearchTagClick("Cold Outreach")}
            className="cursor-pointer font-medium text-blue-600 hover:underline"
          >
            Cold Calling Scripts
          </button>
          <span>•</span>
          <button
            type="button"
            onClick={() => handleSearchTagClick("Subcontractor")}
            className="cursor-pointer font-medium text-blue-600 hover:underline"
          >
            Subcontractor 1099
          </button>
          <span>•</span>
          <button
            type="button"
            onClick={() => handleSearchTagClick("Quality Control")}
            className="cursor-pointer font-medium text-blue-600 hover:underline"
          >
            Inspection SOP
          </button>
          <span>•</span>
          <button
            type="button"
            onClick={() => handleSearchTagClick("ISSA")}
            className="cursor-pointer font-medium text-blue-600 hover:underline"
          >
            ISSA Rates
          </button>
        </div>
      </div>
    </form>
  );
}
